/**
 * ExternalActionCard - v2 content for external action types.
 *
 * Renders actions that direct users to complete tasks in external systems.
 * Shows description, context_message, and optionally an embedded entity brief.
 * Title is rendered by the parent ActionAccordion shell via the registry's
 * getExternalActionTitle(), which reads action_params.title from the payload.
 */

import { useMemo } from 'react';

import { useProposalSurface } from '@/contexts/ProposalSurfaceContext';
import { MarkdownContent } from '@/lib/crm/components/MarkdownContent';
import { cx } from '@/utils/cx';

import type { ExternalActionPayload } from '../../lib/proposals/core/types';
import { isExternalActionPayload } from '../../lib/proposals/core/types';
import { ExternalLinkIcon } from '../../lib/proposals/registry/icons';
import type { ActionRendererProps } from '../../lib/proposals/registry/types';
import { EmbeddedResearchBrief } from './ExternalActionCard.EmbeddedResearchBrief';
import { ActionError } from './shared';
import { ACTION_CARD_BODY_CLASS } from './shared/actionCardLayout';

// ─── URL validation ─────────────────────────────────────────────────────────

const SAFE_URL_PROTOCOLS = new Set(['http:', 'https:', 'mailto:']);

function isSafeUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    return SAFE_URL_PROTOCOLS.has(parsed.protocol);
  } catch {
    return false;
  }
}

// ─── Entity type dispatch table ──────────────────────────────────────────────

const EMBEDDED_ENTITY_RENDERERS = new Set(['ahoy_research']);

// ─── Component ───────────────────────────────────────────────────────────────

export function ExternalActionCard({
  action,
  mode,
  orgId,
  proposalId,
}: ActionRendererProps) {
  // All hooks must run before any conditional return — keep them at the top.
  const { surface } = useProposalSurface();

  // Frontend-side filter: pick the first ref whose type we know how to render.
  // The backend returns refs ordered by entity created_at desc, so taking the
  // first match yields "the freshest renderable entity" — the same outcome the
  // backend used to enforce server-side, but without the backend needing to
  // know which types this UI can draw.
  const embeddedEntity = useMemo(() => {
    const refs = action.entity_refs ?? [];
    return (
      refs.find(ref => EMBEDDED_ENTITY_RENDERERS.has(ref.entity_type_name)) ??
      null
    );
  }, [action.entity_refs]);

  if (!isExternalActionPayload(action.payload)) {
    return (
      <ActionError message='Invalid external action payload' mode={mode} />
    );
  }

  const payload = action.payload as ExternalActionPayload;
  const { description, url, action_label } = payload.action_params;

  // Pre-compute action link fallback to avoid nested ternary
  const actionLinkFallback =
    url && isSafeUrl(url) ? (
      <a
        href={url}
        target='_blank'
        rel='noopener noreferrer'
        className='inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-[var(--color-text-primary_on-brand)] bg-[var(--color-brand-600)] hover:bg-[var(--color-brand-700)] rounded-lg transition-colors'
      >
        <ExternalLinkIcon className='w-4 h-4' />
        {action_label || 'Open link'}
      </a>
    ) : (
      <p className='text-xs text-fg-tertiary italic'>
        No direct link available – manual action required
      </p>
    );

  return (
    <div className={cx(ACTION_CARD_BODY_CLASS, 'space-y-3')}>
      {/* Description */}
      {description && (
        <MarkdownContent
          className='text-sm text-fg-secondary'
          content={description}
          orgId={orgId}
          size='compact'
          resolvedSlots={action.enriched_data?.resolved_slots}
        />
      )}

      {/* Context message */}
      {payload.context_message && (
        <MarkdownContent
          className='text-sm text-fg-secondary'
          content={payload.context_message}
          orgId={orgId}
          size='compact'
          resolvedSlots={action.enriched_data?.resolved_slots}
        />
      )}

      {/* Embedded entity brief or action link button.
       * The renderer-set membership check already happened in the `embeddedEntity`
       * memo above, so by the time we reach this branch we know the type is one
       * we know how to render. The remaining gate is just `orgId` availability,
       * which is needed for the CRM fetch inside the brief. */}
      {embeddedEntity !== null && orgId !== undefined ? (
        <EmbeddedResearchBrief
          entityId={embeddedEntity.entity_id}
          orgId={orgId}
          proposalId={proposalId}
          surface={surface}
          fallback={actionLinkFallback}
        />
      ) : (
        actionLinkFallback
      )}
    </div>
  );
}
